import { useState } from "react";
import { CalendarClock, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { diasParaExclusao } from "@bookdental/schemas";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/lib/auth";
import { formatFileSize } from "@/lib/files-format";
import { ConfirmarExclusaoDialog } from "./ConfirmarExclusaoDialog";
import { CartaoDeExportacao } from "./CartaoDeExportacao";

const plural = (n: number, um: string, varios: string) => `${n.toLocaleString("pt-BR")} ${n === 1 ? um : varios}`;

/**
 * Configurações › Excluir organização (só o proprietário). Explica o que acontece, mostra o que
 * some e agenda a exclusão — 7 dias só leitura, com Desfazer, antes de apagar de vez.
 */
export function ExclusaoSecao() {
  const { user } = useAuth();
  const org = user?.activeOrganization;
  const utils = trpc.useUtils();
  const previa = trpc.organization.exclusao.previa.useQuery();
  const conta = trpc.privacidade.resumo.useQuery();
  const [confirmando, setConfirmando] = useState(false);
  const desfazer = trpc.organization.exclusao.desfazer.useMutation({
    onSuccess: () => {
      toast.success("Exclusão desfeita. A organização voltou ao normal.");
      utils.invalidate();
    },
    onError: (e) => toast.error(e.message),
  });

  if (!org || previa.isLoading || conta.isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-brand-600" />
      </div>
    );
  }
  const p = previa.data;
  const prevista = org.exclusaoPrevistaPara ? new Date(org.exclusaoPrevistaPara) : null;

  return (
    <div className="space-y-5 px-6 py-5">
      {prevista ? (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 dark:border-red-500/30 dark:bg-red-500/10">
          <div className="flex items-start gap-2.5 text-sm text-red-800 dark:text-red-300">
            <CalendarClock className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              <b>Exclusão agendada para {prevista.toLocaleDateString("pt-BR")}</b> ({plural(diasParaExclusao(prevista, new Date()), "dia", "dias")}). Até lá a
              organização é só leitura para todos.
            </span>
          </div>
          <button
            type="button"
            disabled={desfazer.isPending}
            onClick={() => desfazer.mutate()}
            className="inline-flex h-9 items-center gap-1.5 rounded-md bg-white px-4 text-sm font-semibold text-red-700 ring-1 ring-red-200 hover:bg-red-50 disabled:opacity-60 dark:bg-card"
          >
            {desfazer.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Desfazer exclusão
          </button>
        </div>
      ) : null}

      <section className="space-y-2 text-sm text-zinc-700 dark:text-foreground/90">
        <h3 className="text-sm font-semibold text-zinc-900 dark:text-foreground">O que acontece</h3>
        <ol className="list-decimal space-y-1 pl-5">
          <li>A organização fica <b>só leitura por 7 dias</b>. Dá para ver tudo, mas ninguém cria nem altera nada. Você pode desfazer até lá.</li>
          <li>Os membros recebem um aviso no DentalBook e por e-mail.</li>
          <li>
            No fim do prazo os dados são apagados e os membros saem.
            {p?.resultado === "encerrada" &&
              " Como há pedidos de prótese trocados com outra organização, ela é encerrada em vez de apagada: esses pedidos ficam, com o paciente anonimizado."}
          </li>
        </ol>
      </section>

      {p && (
        <section className="rounded-lg border border-zinc-200 p-4 dark:border-border">
          <h3 className="mb-2 text-sm font-semibold text-zinc-900 dark:text-foreground">O que vai ser apagado</h3>
          <ul className="grid gap-1 text-sm text-zinc-700 sm:grid-cols-2 dark:text-foreground/90">
            <li>{plural(p.pacientes, "paciente", "pacientes")}</li>
            <li>{plural(p.consultas, "consulta", "consultas")}</li>
            <li>
              {plural(p.arquivos, "arquivo", "arquivos")}
              {p.arquivos > 0 && <span className="text-zinc-500"> ({formatFileSize(p.tamanhoDosArquivos)})</span>}
            </li>
            <li>{plural(p.pedidos, "pedido de prótese", "pedidos de prótese")}</li>
            <li>{plural(p.membros, "membro sai", "membros saem")} da equipe</li>
          </ul>
        </section>
      )}

      <CartaoDeExportacao />

      {!prevista && conta.data && (
        <div className="flex justify-end border-t border-zinc-100 pt-4 dark:border-border">
          <button
            type="button"
            onClick={() => setConfirmando(true)}
            className="inline-flex h-9 items-center gap-1.5 rounded-md bg-red-600 px-4 text-sm font-semibold text-white hover:bg-red-700"
          >
            <Trash2 className="h-4 w-4" />
            Excluir organização
          </button>
        </div>
      )}
      {conta.data && (
        <ConfirmarExclusaoDialog aberto={confirmando} nome={org.name} conta={conta.data} onFechar={() => setConfirmando(false)} />
      )}
    </div>
  );
}
